import { apiCustomer } from "@/api"
import { delay } from "@/lib"
import {
  genericRejectionMessage,
  messageForRejectedValidation,
} from "@/lib/checkout-errors"
import { errorMessage } from "@/lib/i18n/error-messages"
import { OrderSagaStatusResponse, PriceReview, SagaRejectedValidation } from "@/types/external"

const CHECKOUT_LONGPOLL_WAIT_S = 25
const CHECKOUT_LONGPOLL_REQUEST_TIMEOUT_MS = (CHECKOUT_LONGPOLL_WAIT_S + 10) * 1000
const CHECKOUT_MIN_LOOP_GAP_MS = 500
const CHECKOUT_POLL_TIMEOUT_MS = 300000

export type PixQrData = {
  qrCode: string
  qrCodeBase64: string
  txid: string
  expiresAt: string
}

function getSagaFailureCode(sagaStatus: OrderSagaStatusResponse): string | null {
  const rejections = sagaStatus.rejectedValidations ?? []
  return rejections.find((validation) => validation.code)?.code ?? null
}

function getSagaFailureMessage(
  sagaStatus: OrderSagaStatusResponse,
  isPixOnline: boolean,
): string | null {
  if (isPixOnline && sagaStatus.isCancelled) {
    return null
  }
  if (isPixOnline && sagaStatus.isTerminal && sagaStatus.outcome?.toLowerCase() === "cancelled") {
    return null
  }

  if (sagaStatus.isCancelled) {
    return errorMessage("cancelledDuringValidation")
  }

  if (sagaStatus.isRejected) {
    const rejections = sagaStatus.rejectedValidations ?? []
    const rejection = rejections.find((validation) => validation.code) ?? rejections[0]
    if (rejection) {
      return messageForRejectedValidation(rejection)
    }

    return genericRejectionMessage()
  }

  if (sagaStatus.isTerminal && sagaStatus.outcome?.toLowerCase() === "cancelled") {
    return errorMessage("cancelledDuringValidation")
  }

  if (sagaStatus.isTerminal && sagaStatus.outcome?.toLowerCase() === "rejected") {
    return genericRejectionMessage()
  }

  return null
}

function isSagaSuccess(sagaStatus: OrderSagaStatusResponse): boolean {
  if (!sagaStatus.found || !sagaStatus.isTerminal) {
    return false
  }

  return sagaStatus.outcome?.toLowerCase() === "confirmed"
}

function extractPixQrData(sagaStatus: OrderSagaStatusResponse): PixQrData | null {
  if (sagaStatus.pixQrCode && sagaStatus.pixTxid && sagaStatus.pixExpiresAt) {
    return {
      qrCode: sagaStatus.pixQrCode,
      qrCodeBase64: sagaStatus.pixQrCodeBase64 ?? "",
      txid: sagaStatus.pixTxid,
      expiresAt: sagaStatus.pixExpiresAt,
    }
  }
  return null
}

/**
 * A parked price review is NOT a failure: the saga is alive and waiting for the
 * customer, exactly like a PIX QR waits for the payment. Surfacing it before the
 * terminal checks is what stops the poll from reading it as a rejection.
 */
function extractPriceReview(sagaStatus: OrderSagaStatusResponse): PriceReview | null {
  const review = sagaStatus.priceReview
  if (!review || review.lines.length === 0) return null
  return review
}

/** What the saga last reported, kept for diagnostics (PIX payloads and price review stripped). */
export type SagaSnapshot = {
  sagaId?: string
  sagaType?: string
  state?: string
  outcome?: string
  updatedAt?: string
  isTerminal: boolean
  isRejected: boolean
  isCancelled: boolean
  rejectedValidations: Pick<SagaRejectedValidation, "step" | "reason" | "code" | "details">[]
  /** How many status responses the loop read before resolving. */
  polls: number
}

export type CheckoutResolution =
  | { success: true; timedOut: false; failMessage: null; failCode: null; pixCancelled: false; saga: SagaSnapshot }
  | { success: false; timedOut: true; failMessage: null; failCode: null; pixCancelled: false; saga: SagaSnapshot | null }
  | {
      success: false
      timedOut: false
      failMessage: string
      /** Stable code behind the refusal, when the saga carried one. */
      failCode: string | null
      pixCancelled: false
      saga: SagaSnapshot
    }
  | { success: false; timedOut: false; failMessage: null; failCode: null; pixCancelled: true; saga: SagaSnapshot }

function snapshotSaga(sagaStatus: OrderSagaStatusResponse, polls: number): SagaSnapshot {
  return {
    sagaId: sagaStatus.sagaId,
    sagaType: sagaStatus.sagaType,
    state: sagaStatus.state,
    outcome: sagaStatus.outcome,
    updatedAt: sagaStatus.updatedAt,
    isTerminal: sagaStatus.isTerminal,
    isRejected: sagaStatus.isRejected,
    isCancelled: sagaStatus.isCancelled,
    rejectedValidations: (sagaStatus.rejectedValidations ?? []).map(({ step, reason, code, details }) => ({
      step,
      reason,
      code,
      details,
    })),
    polls,
  }
}

/**
 * Polls the order saga status after checkout until the saga reaches a
 * terminal state or the timeout elapses. Rejection messages are resolved
 * from the stable validation code (then step) — see checkout-errors.ts.
 *
 * For PIX Online orders, the callback `onPixQrReady` is invoked as soon as
 * the QR code fields appear in the saga status response (before terminal).
 * When the PIX QR expires and the saga is cancelled, `pixCancelled: true` is
 * returned so the caller can offer the reconcile button.
 */
export async function waitForCheckoutResolution(
  orderId: string,
  options?: {
    isPixOnline?: boolean
    onPixQrReady?: (data: PixQrData) => void
    /** Fired once the merchant reprices the cart upward and the saga parks. */
    onPriceReview?: (review: PriceReview) => void
    /** Fired for every failed poll request, which the loop otherwise swallows and retries. */
    onPollError?: (error: unknown, attempt: number) => void
  },
): Promise<CheckoutResolution> {
  const startedAt = Date.now()
  const isPixOnline = options?.isPixOnline ?? false
  let pixQrNotified = false
  let priceReviewNotified = false
  let sinceToken = ""
  let pollErrors = 0
  let polls = 0
  let lastSeen: SagaSnapshot | null = null

  while (Date.now() - startedAt <= CHECKOUT_POLL_TIMEOUT_MS) {
    const iterationStart = Date.now()

    let sagaStatus: OrderSagaStatusResponse
    try {
      const response = await apiCustomer.get<OrderSagaStatusResponse>(`/orders/${orderId}/saga-status`, {
        params: { wait: CHECKOUT_LONGPOLL_WAIT_S, since: sinceToken },
        timeout: CHECKOUT_LONGPOLL_REQUEST_TIMEOUT_MS,
      })
      sagaStatus = response.data
    } catch (error) {
      pollErrors += 1
      options?.onPollError?.(error, pollErrors)
      await delay(CHECKOUT_MIN_LOOP_GAP_MS)
      continue
    }

    sinceToken = sagaStatus.changeToken ?? sinceToken
    polls += 1

    if (sagaStatus.found) {
      lastSeen = snapshotSaga(sagaStatus, polls)
      if (isPixOnline && !pixQrNotified && options?.onPixQrReady) {
        const pixData = extractPixQrData(sagaStatus)
        if (pixData) {
          pixQrNotified = true
          options.onPixQrReady(pixData)
        }
      }

      if (!priceReviewNotified && options?.onPriceReview) {
        const review = extractPriceReview(sagaStatus)
        if (review) {
          priceReviewNotified = true
          options.onPriceReview(review)
        }
      }

      if (isSagaSuccess(sagaStatus)) {
        return {
          success: true,
          timedOut: false,
          failMessage: null,
          failCode: null,
          pixCancelled: false,
          saga: lastSeen,
        }
      }

      if (isPixOnline && sagaStatus.isTerminal &&
          (sagaStatus.isCancelled || sagaStatus.outcome?.toLowerCase() === "cancelled")) {
        return {
          success: false,
          timedOut: false,
          failMessage: null,
          failCode: null,
          pixCancelled: true,
          saga: lastSeen,
        }
      }

      const failMessage = getSagaFailureMessage(sagaStatus, isPixOnline)
      if (failMessage) {
        return {
          success: false,
          timedOut: false,
          failMessage,
          failCode: getSagaFailureCode(sagaStatus),
          pixCancelled: false,
          saga: lastSeen,
        }
      }

      if (sagaStatus.isTerminal) {
        return {
          success: false,
          timedOut: false,
          failMessage: genericRejectionMessage(),
          failCode: getSagaFailureCode(sagaStatus),
          pixCancelled: false,
          saga: lastSeen,
        }
      }
    }

    const elapsed = Date.now() - iterationStart
    if (elapsed < CHECKOUT_MIN_LOOP_GAP_MS) {
      await delay(CHECKOUT_MIN_LOOP_GAP_MS - elapsed)
    }
  }

  return {
    success: false,
    timedOut: true,
    failMessage: null,
    failCode: null,
    pixCancelled: false,
    saga: lastSeen,
  }
}
